import { ABP, AbpLocalStorageService, ConfigStateService, ListService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { FILTER_STATE_KEY_PREFIX, isFilterStateKey } from 'src/app/constants/filter-page-keys';
import { BrowserSessionService } from './browser-session.service';

/** Bump to silently drop every record written by an older shape of the envelope. */
const VERSION = 'v1';

/** Records older than this are dropped on restore. */
const TTL_MS = 12 * 60 * 60 * 1000;

/** Marker of a serialized Date, see tagDates/reviveDates. */
const DATE_TAG = 'date';

/** Guards against pathological / cyclic filter objects while tagging dates. */
const MAX_TAG_DEPTH = 20;

/**
 * Preset persisted instead of the absolute dates of the untouched "last 7 days" default
 * range, so the range is recomputed relative to today on the next visit.
 */
export const FILTER_DATE_PRESET_LAST_7_DAYS = 'last7days';

export interface SortState {
  key: string;
  order: string;
}

export interface RestoredFilterState<T> {
  filters: T;
  sort?: SortState;
  dateRangePreset?: string;
}

/** Outcome of a lookup prune: the value to keep and whether it had to be dropped. */
export interface PruneResult<T> {
  value: T | null;
  changed: boolean;
}

interface FilterStateEnvelope<T> extends RestoredFilterState<T> {
  v: string;
  userId: string;
  sessionId: string;
  savedAt: number;
}

/**
 * Per-user, per-page persistence of list filters.
 *
 * Records live in localStorage (shared across tabs, survive a refresh) but are scoped to the
 * browser session id produced by BrowserSessionService, to the current user and to a 12 hour
 * TTL, so they disappear when the browser is closed, when somebody else logs in or when they
 * simply get old. Nothing here may throw: persistence is a convenience, never a requirement.
 */
@Injectable({ providedIn: 'root' })
export class FilterStateService {
  constructor(
    private readonly localStorage: AbpLocalStorageService,
    private readonly configState: ConfigStateService,
    private readonly session: BrowserSessionService,
  ) {}

  save<T>(
    pageKey: string,
    filters: T,
    options?: { sort?: SortState; dateRangePreset?: string },
  ): void {
    const userId = this.currentUserId;
    if (!userId) return;

    const envelope: FilterStateEnvelope<T> = {
      v: VERSION,
      userId,
      sessionId: this.session.sessionId,
      savedAt: Date.now(),
      filters,
      sort: options?.sort,
      dateRangePreset: options?.dateRangePreset,
    };

    try {
      this.localStorage.setItem(
        this.buildKey(pageKey, userId),
        JSON.stringify(this.tagDates(envelope)),
      );
    } catch {
      // ignore: private mode, quota exceeded, ...
    }
  }

  restore<T>(pageKey: string): RestoredFilterState<T> | null {
    const userId = this.currentUserId;
    if (!userId) return null;

    const storageKey = this.buildKey(pageKey, userId);

    try {
      const raw = this.localStorage.getItem(storageKey);
      if (!raw) return null;

      const envelope = JSON.parse(raw, reviveDates) as FilterStateEnvelope<T>;
      const isUsable =
        !!envelope &&
        envelope.v === VERSION &&
        envelope.userId === userId &&
        envelope.sessionId === this.session.sessionId &&
        typeof envelope.savedAt === 'number' &&
        Date.now() - envelope.savedAt <= TTL_MS &&
        !!envelope.filters;

      if (!isUsable) {
        this.localStorage.removeItem(storageKey);
        return null;
      }

      return {
        filters: envelope.filters,
        sort: envelope.sort,
        dateRangePreset: envelope.dateRangePreset,
      };
    } catch {
      this.clearKey(storageKey);
      return null;
    }
  }

  clear(pageKey: string): void {
    const userId = this.currentUserId;
    if (!userId) return;

    this.clearKey(this.buildKey(pageKey, userId));
  }

  /** Removes every persisted filter record (logout, "Clear Cache", new browser session). */
  clearAll(): void {
    try {
      const keys: string[] = [];
      for (let index = 0; index < this.localStorage.length; index++) {
        const key = this.localStorage.key(index);
        if (isFilterStateKey(key)) {
          keys.push(key);
        }
      }
      keys.forEach(key => this.localStorage.removeItem(key));
    } catch {
      // ignore: private mode / disabled storage
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // Page integration helpers
  // ═══════════════════════════════════════════════════════════════

  /**
   * Restores a page's record and pushes the saved sort into the ListService.
   * MUST be called before `hookToQuery`, which fires the first query immediately.
   * The returned sort still has to be applied to the datatable in `ngAfterViewInit`
   * (see applySortToTable) or the sort arrow will not show.
   */
  restoreInto<T>(pageKey: string, list: ListService): RestoredFilterState<T> | null {
    const restored = this.restore<T>(pageKey);
    if (restored?.sort?.key) {
      list.sortKey = restored.sort.key;
      list.sortOrder = restored.sort.order;
    }
    return restored;
  }

  /** Persists the filters together with the current sort of the list. */
  saveFrom<T>(pageKey: string, filters: T, list: ListService, dateRangePreset?: string): void {
    this.save(pageKey, filters, { sort: this.getSort(list), dateRangePreset });
  }

  /**
   * Persists a sort change WITHOUT touching the filters of the stored record: the filter card
   * mutates `filters` in place, so at this point they may contain edits the user never applied.
   *
   * When no record exists yet there is nothing to merge into, and the current filters are stored
   * along with the sort - the only case where unapplied edits can still be persisted.
   */
  saveSort<T>(
    pageKey: string,
    sort: SortState | undefined,
    fallback: { filters: T; dateRangePreset?: string },
  ): void {
    const state = this.restore<T>(pageKey) ?? fallback;

    this.save(pageKey, state.filters, { sort, dateRangePreset: state.dateRangePreset });
  }

  /**
   * Persists the page when the user changes only the sort. `query$` also emits on paging and
   * on the free text search, so the sorting is compared with the previous emission and only a
   * real sort change triggers a write.
   */
  trackSortChanges<T>(
    pageKey: string,
    list: ListService,
    snapshot: () => { filters: T; dateRangePreset?: string },
  ): Subscription {
    let lastSorting: string | undefined;
    let seenFirstQuery = false;

    return list.query$.subscribe((query: ABP.PageQueryParams) => {
      const sorting = query?.sorting;

      if (!seenFirstQuery) {
        seenFirstQuery = true;
        lastSorting = sorting;
        return;
      }

      if (sorting === lastSorting) return;
      lastSorting = sorting;

      this.saveSort(pageKey, this.getSort(list), snapshot());
    });
  }

  getSort(list: ListService): SortState | undefined {
    return list?.sortKey && list?.sortOrder
      ? { key: list.sortKey, order: list.sortOrder }
      : undefined;
  }

  /**
   * ABP's datatable directive never pushes the list state back into the table, so the restored
   * sort has to be written on the table by hand for the header arrow to appear.
   */
  applySortToTable(
    table: DatatableComponent | null | undefined,
    sort: SortState | null | undefined,
  ): void {
    if (!table || !sort?.key || !sort?.order) return;

    table.sorts = [{ prop: sort.key, dir: sort.order.toLowerCase() }];
  }

  /**
   * Drops a restored lookup value that no longer exists in the loaded options.
   * Compared as strings on purpose: the lookups mix numeric ids with string ids.
   *
   * `changed` tells the caller that a value was really dropped. The lookups resolve AFTER the
   * first query has been fired with the restored value, so the caller must re-query once -
   * otherwise the grid keeps showing the result of a query made with the dropped id.
   */
  pruneMissingValue<T>(value: T | null | undefined, options: readonly unknown[]): PruneResult<T> {
    if (value === null || value === undefined) return { value: null, changed: false };

    return this.contains(options, value)
      ? { value, changed: false }
      : { value: null, changed: true };
  }

  /** Multi-select variant of pruneMissingValue; empties down to null when nothing survives. */
  pruneMissingValues<T>(
    values: T[] | null | undefined,
    options: readonly unknown[],
  ): PruneResult<T[]> {
    if (!values?.length) return { value: values ?? null, changed: false };

    const kept = values.filter(value => this.contains(options, value));
    if (kept.length === values.length) return { value: values, changed: false };

    return { value: kept.length ? kept : null, changed: true };
  }

  private contains(options: readonly unknown[], value: unknown): boolean {
    return (options ?? []).some(option => String(option) === String(value));
  }

  // ═══════════════════════════════════════════════════════════════
  // Internals
  // ═══════════════════════════════════════════════════════════════

  private get currentUserId(): string | null {
    try {
      const id = this.configState.getDeep('currentUser.id');
      return typeof id === 'string' && id.length > 0 ? id : null;
    } catch {
      return null;
    }
  }

  private buildKey(pageKey: string, userId: string): string {
    return `${FILTER_STATE_KEY_PREFIX}.${VERSION}:${userId}:${pageKey}`;
  }

  private clearKey(storageKey: string): void {
    try {
      this.localStorage.removeItem(storageKey);
    } catch {
      // ignore: private mode / disabled storage
    }
  }

  /**
   * Replaces every Date by a tagged object BEFORE stringify. A `JSON.stringify` replacer
   * cannot be used here: it receives the value already converted by `Date.prototype.toJSON`,
   * so the Date would be indistinguishable from a plain ISO string.
   */
  private tagDates(value: any, depth = 0): any {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : { __t: DATE_TAG, v: value.toISOString() };
    }

    if (value === null || typeof value !== 'object' || depth >= MAX_TAG_DEPTH) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(item => this.tagDates(item, depth + 1));
    }

    const tagged: Record<string, any> = {};
    Object.keys(value).forEach(key => {
      tagged[key] = this.tagDates(value[key], depth + 1);
    });

    return tagged;
  }
}

/** Turns the tagged objects written by tagDates back into real Date instances. */
function reviveDates(_key: string, value: any): any {
  if (value && typeof value === 'object' && value.__t === DATE_TAG && typeof value.v === 'string') {
    return new Date(value.v);
  }

  return value;
}
